import GenericTable from "components/table";
import { TechnicalNote, TechnicalNoteStatus } from "types";
import React, { useState, useEffect, useRef } from "react";
import { Check, X, Clock, FileText, Bot, Trash } from "lucide-react";
import { Dialog } from "components/dialog";
import { useConfirmation } from "components/dialog/confirmation";
import api from "api/axios";
import toast from "react-hot-toast";
import { DATE_TIME_FORMAT } from "constants/date";
import dayjs from "dayjs";
import { handleAxiosAndCaughtError } from "utils";
import TooltipHorizon from "components/tooltip";
import { useSidebarCountsContext } from "contexts/SidebarCountsContext";
import { useNotificationRefresh } from "contexts/NotificationContext";
import { useTranslation } from "react-i18next";

type TechnicalNotesTableProps = {
  tableData: TechnicalNote[];
  fetchTechnicalNotes: (params?: any) => Promise<void>;
  total?: number;
  initialTechnicalNoteId?: string | null;
};

const TechnicalNotesTable: React.FC<TechnicalNotesTableProps> = ({ tableData, fetchTechnicalNotes, initialTechnicalNoteId }) => {
  const { t } = useTranslation();
  const [updatingRowId, setUpdatingRowId] = React.useState<string | null>(null);
  const { refetchTechnicalNotesCount } = useSidebarCountsContext();
  const { triggerNotiRefresh } = useNotificationRefresh();

  const { confirm } = useConfirmation();

  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedTechnicalNote, setSelectedTechnicalNote] = useState<TechnicalNote | null>(null);
  const openedInitialIdRef = useRef<string | null>(null);

  const handleApproveTechnicalNote = async (technicalNoteId: string) => {
    setUpdatingRowId(technicalNoteId);
    try {
      await api.post(`/technical-notes/${technicalNoteId}/approve`);
      await fetchTechnicalNotes();
      await refetchTechnicalNotesCount();
      // Trigger immediate notification count refresh
      triggerNotiRefresh();
      toast.success(t("library.technicalNoteApprovedSuccessfully"));
      setUpdatingRowId(null);
      setIsViewDialogOpen(false);
    } catch (err) {
      setUpdatingRowId(null);
      handleAxiosAndCaughtError(err);
    }
  };

  const handleRejectTechnicalNote = async (technicalNoteId: string) => {
    setUpdatingRowId(technicalNoteId);
    try {
      await api.post(`/technical-notes/${technicalNoteId}/reject`);
      await fetchTechnicalNotes();
      await refetchTechnicalNotesCount();
      // Trigger immediate notification count refresh
      triggerNotiRefresh();
      toast.success(t("library.technicalNoteRejectedSuccessfully"));
      setUpdatingRowId(null);
      setIsViewDialogOpen(false);
    } catch (err) {
      setUpdatingRowId(null);
      handleAxiosAndCaughtError(err);
    }
  };

  const handleDeleteTechnicalNote = async (technicalNoteId: string) => {
    setUpdatingRowId(technicalNoteId);
    try {
      await api.delete(`/technical-notes/${technicalNoteId}`);
      await fetchTechnicalNotes();
      toast.success(t("library.technicalNoteDeletedSuccessfully"));
      setUpdatingRowId(null);
    } catch (err) {
      setUpdatingRowId(null);
      handleAxiosAndCaughtError(err);
    }
  };

  const handleViewTechnicalNote = (technicalNote: TechnicalNote) => {
    setSelectedTechnicalNote(technicalNote);
    setIsViewDialogOpen(true);
  };

  // Reset ref when dialog is closed
  const handleCloseDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedTechnicalNote(null);
    // Reset the ref when dialog is manually closed
    if (openedInitialIdRef.current === initialTechnicalNoteId) {
      openedInitialIdRef.current = null;
    }
  };

  // Auto-open dialog if initialTechnicalNoteId is provided (from notification)
  useEffect(() => {
    if (initialTechnicalNoteId && 
        tableData.length > 0 && 
        openedInitialIdRef.current !== initialTechnicalNoteId) {
      const technicalNote = tableData.find(note => note.id === initialTechnicalNoteId);
      if (technicalNote) {
        openedInitialIdRef.current = initialTechnicalNoteId;
        handleViewTechnicalNote(technicalNote);
      }
    }
  }, [initialTechnicalNoteId, tableData]);

  const handleApproveWithConfirmation = async (technicalNoteId: string) => {
    const confirmed = await confirm({
      title: t("library.approveTechnicalNote"),
      message: t("library.confirmApproveTechnicalNote"),
      confirmText: t("library.approve"),
      cancelText: t("common.cancel"),
      variant: "default",
    });

    if (confirmed) {
      await handleApproveTechnicalNote(technicalNoteId);
    }
  };

  const handleRejectWithConfirmation = async (technicalNoteId: string) => {
    const confirmed = await confirm({
      title: t("library.rejectTechnicalNote"),
      message: t("library.confirmRejectTechnicalNote"),
      confirmText: t("library.reject"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      await handleRejectTechnicalNote(technicalNoteId);
    }
  };

  const handleDeleteWithConfirmation = async (technicalNoteId: string, question: string) => {
    const confirmed = await confirm({
      title: t("library.deleteTechnicalNote"),
      message: t("library.confirmDeleteTechnicalNote", { question }),
      confirmText: t("library.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      await handleDeleteTechnicalNote(technicalNoteId);
    }
  };

  const isTechnicalNotePending = (technicalNote: TechnicalNote | null) => {
    return technicalNote?.status === TechnicalNoteStatus.PENDING;
  };

  const getStatusColor = (status: TechnicalNoteStatus) => {
    switch (status) {
      case TechnicalNoteStatus.PENDING:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case TechnicalNoteStatus.APPROVED:
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case TechnicalNoteStatus.REJECTED:
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const getStatusIcon = (status: TechnicalNoteStatus) => {
    switch (status) {
      case TechnicalNoteStatus.PENDING:
        return <Clock size={12} />;
      case TechnicalNoteStatus.APPROVED:
        return <Check size={12} />;
      case TechnicalNoteStatus.REJECTED:
        return <X size={12} />;
      default:
        return <Clock size={12} />;
    }
  };

  const columns = [
    {
      accessorKey: "question",
      header: () => <p>{t("library.question")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white max-w-xs truncate">
          {info.getValue() || t("library.noQuestionProvided")}
        </p>
      ),
    },
    {
      accessorKey: "answer",
      header: () => <p>{t("library.answer")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white max-w-xs truncate">
          {info.getValue() || t("library.noAnswerProvided")}
        </p>
      ),
    },
    {
      accessorKey: "status",
      header: () => <p>{t("library.status")}</p>,
      cell: (info: any) => {
        const status = info.getValue() as TechnicalNoteStatus;
        const statusKey = `library.statusValues.${status.toLowerCase()}` as const;
        return (
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(status)}`}>
            {getStatusIcon(status)}
            {t(statusKey)}
          </span>
        );
      },
      meta: {
        filterable: true,
      },
      filterFn: 'multiSelect',
    },
    {
      accessorKey: "agent_id",
      header: () => <p>{t("library.agent")}</p>,
      cell: (info: any) => (
        <div className="flex items-center gap-1">
          <Bot size={12} className="text-gray-500" />
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue() || t("library.unknownAgent")}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "created_by",
      header: () => <p>{t("library.createdBy")}</p>,
      cell: (info: any) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() || t("library.unknownUser")}
        </p>
      ),
    },
    {
      accessorKey: "created_at",
      header: () => <p>{t("library.createdDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {dayjs(info.getValue()).format(DATE_TIME_FORMAT)}
        </p>
      ),
    },
    {
      accessorKey: "updated_at",
      header: () => <p>{t("library.updatedDate")}</p>,
      cell: (info: any) => (
        <p className="text-sm text-navy-700 dark:text-white">
          {dayjs(info.getValue()).format(DATE_TIME_FORMAT)}
        </p>
      ),
    },
    {
      accessorKey: "actions",
      header: () => <p>{t("library.actions")}</p>,
      cell: (info: any) => {
        const row = info.row.original;
        const isUpdating = updatingRowId === row.id;
        
        return (
          <div className="flex items-center gap-2">
            <TooltipHorizon
              trigger={
                <button
                  disabled={isUpdating}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteWithConfirmation(row.id, row.question);
                  }}
                  className="rounded-md p-2 text-red-500 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-900"
                >
                  {isUpdating ? (
                    <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-red-500" />
                  ) : (
                    <Trash size={16} />
                  )}
                </button>
              }
              content={<div>{t("library.removeTechnicalNote")}</div>}
              placement="top"
              extra=""
            />
          </div>
        );
      },
    },
  ];

  const dataWithActions = tableData.map((row) => ({
    ...row,
    updatingRowId,
  }));

  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return updatingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  return (
    <>
      <GenericTable
        data={dataWithActions}
        columns={columns as any}
        searchPlaceholder={t("library.searchTechnicalNotes")}
        debug={true}
        renderRowClassName={renderRowClassName}
        onRowClick={(item) => {
          handleViewTechnicalNote(item);
        }}
      />
      
      {/* View Technical Note Dialog */}
      <Dialog isOpen={isViewDialogOpen} setIsOpen={handleCloseDialog} outsideClickDisabled={true} backdropHandlerDisabled={true}>
        <div className="flex min-h-[60vh] w-full max-w-[700px] flex-col gap-4 sm:gap-6">
          <div className="border-b border-gray-200 pb-3 sm:pb-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
                {t("library.technicalNoteDetails")}
              </h2>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
              {selectedTechnicalNote && isTechnicalNotePending(selectedTechnicalNote) 
                ? t("library.reviewAndApproveOrReject") 
                : t("library.viewTechnicalNoteReadOnly")}
            </p>
          </div>

          {selectedTechnicalNote ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("library.agent")}
                  </label>
                  <div className="mt-1 flex items-center gap-1">
                    <Bot size={14} className="text-gray-500" />
                    <p className="text-sm text-gray-900 dark:text-white">
                      {selectedTechnicalNote.agent_id || t("library.unknownAgent")}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("library.status")}
                  </label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(selectedTechnicalNote.status)}`}>
                      {getStatusIcon(selectedTechnicalNote.status)}
                      {t(`library.statusValues.${selectedTechnicalNote.status.toLowerCase()}` as const)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("library.createdBy")}
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {selectedTechnicalNote.created_by || t("library.unknownUser")}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t("library.createdDate")}
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {dayjs(selectedTechnicalNote.created_at).format(DATE_TIME_FORMAT)}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("library.updatedDate")}
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {dayjs(selectedTechnicalNote.updated_at).format(DATE_TIME_FORMAT)}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("library.question")}
                </label>
                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800">
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                    {selectedTechnicalNote.question || t("library.noQuestionProvided")}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("library.answer")}
                </label>
                <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800">
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                    {selectedTechnicalNote.answer || t("library.noAnswerProvided")}
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:gap-3 sm:pt-4">
            {isTechnicalNotePending(selectedTechnicalNote) && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    if (selectedTechnicalNote) {
                      handleApproveWithConfirmation(selectedTechnicalNote.id);
                    }
                  }}
                  className="flex-1 rounded-lg bg-green-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-20"
                >
                  {t("library.approve")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (selectedTechnicalNote) {
                      handleRejectWithConfirmation(selectedTechnicalNote.id);
                    }
                  }}
                  className="flex-1 rounded-lg bg-red-500 px-4 py-2 font-semibold text-white transition-colors hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-20"
                >
                  {t("library.reject")}
                </button>
              </>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default TechnicalNotesTable; 